import React, { useEffect, useState } from "react";
import { getAllPalmares } from "../../services/palmaresServices";

const LastPalmares = () => {
  const [palmares, setPalmares] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 6;

  useEffect(() => {
    const fetchPalmares = async () => {
      try {
        const data = await getAllPalmares();
        const actifs = data.filter((p) => p.is_active); // Filtrage ici
        setPalmares(actifs);
      } catch (error) {
        console.error("Erreur lors de la récupération des palmarès :", error);
      }
    };

    fetchPalmares();
  }, []);

  // Calculer les éléments affichés en fonction de la page actuelle
  const startIndex = currentPage * itemsPerPage;
  const selectedPalmares = palmares.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    // déjà absolu, data URL ou blob
    if (
      /^https?:\/\//i.test(s) ||
      s.startsWith("data:") ||
      s.startsWith("blob:")
    )
      return s;
    // chemins d'assets front (ex: /img/...): on laisse tel quel
    if (!s.startsWith("/uploads")) return s;
    // fichiers uploadés côté API
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  return (
    <div className="container mx-auto py-8">
      <h2 className="text-3xl font-extrabold mb-4 text-primary mx-auto text-center font-satoshi uppercase">
        Derniers Palmarès du Club
      </h2>

      {/* Tableau scrollable des derniers palmarès */}
      <div className="mt-6 hidden lg:block">
        <div className="h-96 overflow-y-auto border border-gray-300 rounded shadow-lg">
          <table className="table-auto w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                <th className="border border-gray-300 dark:border-gray-600 p-2 dark:text-white">
                  Adhérents
                </th>
                <th className="border border-gray-300 dark:border-gray-600 p-2 dark:text-white">
                  Date
                </th>
                <th className="border border-gray-300 dark:border-gray-600 p-2 dark:text-white">
                  Compétition
                </th>
                <th className="border border-gray-300 dark:border-gray-600 p-2 dark:text-white">
                  Classement
                </th>
              </tr>
            </thead>
            <tbody>
              {palmares.map((item) => (
                <tr
                  key={item.id}
                  className="border-b border-gray-300 dark:border-gray-600 dark:bg-gray-500"
                >
                  {/* Colonne Adhérent (Image + Nom/Prénom) */}
                  <td className="p-2">
                    <div className="flex items-center space-x-4">
                      <img
                        src={toAbsolute(item.adherent.photo_url)}
                        alt={`${item.adherent.nom} ${item.adherent.prenom}`}
                        className="w-20 h-20 rounded-full object-cover border border-gray-300"
                      />
                      <div className="leading-tight">
                        <div className="font-semibold text-gray-800 dark:text-white">
                          {item.adherent.nom}
                        </div>
                        <div className="italic text-gray-600 dark:text-gray-300">
                          {item.adherent.prenom}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Colonne Date */}
                  <td className="p-2 text-sm align-middle">
                    {new Date(item.date_palmares).toLocaleDateString("fr-FR")}
                  </td>

                  {/* Colonne Compétition */}
                  <td className="p-2 text-sm align-middle">
                    {item.titre_palmares}
                  </td>

                  {/* Colonne Classement */}
                  <td className="p-2 font-bold align-middle">
                    {item.classement_palmares}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mini-cards en mode mobile avec pagination */}
      <div className="lg:hidden px-4 mt-6">
        <div className="grid grid-cols-2 gap-4">
          {selectedPalmares.map((item) => (
            <div
              key={item.id}
              className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-4 flex flex-col items-center text-center"
            >
              {/* Avatar */}
              <img
                src={toAbsolute(item.adherent.photo_url)}
                alt={`${item.adherent.nom} ${item.adherent.prenom}`}
                className="w-32 h-32 rounded-full object-cover mb-4"
              />
              {/* Nom et Prénom */}
              <h3 className="text-sm font-bold text-gray-800 dark:text-white">
                {item.adherent.nom} {item.adherent.prenom}
              </h3>
              {/* Date de la Compétition */}
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {new Date(item.date_palmares).toLocaleDateString("fr-FR")}
              </p>
              {/* Nom de la Compétition */}
              <p className="text-xs text-gray-700 dark:text-gray-300 mt-2">
                {item.titre_palmares}
              </p>
              {/* Classement */}
              <p className="text-lg font-bold text-blue-500 dark:text-blue-400 mt-2">
                {item.classement_palmares}
              </p>
            </div>
          ))}
        </div>

        {/* Pagination */}
        <div className="flex justify-center mt-4">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
            className={`px-4 py-2 mx-2 rounded ${
              currentPage === 0
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-blue-500 hover:bg-blue-600 text-white"
            }`}
            disabled={currentPage === 0}
          >
            Précédent
          </button>
          <button
            onClick={() =>
              setCurrentPage((prev) =>
                startIndex + itemsPerPage < palmares.length ? prev + 1 : prev
              )
            }
            className={`px-4 py-2 mx-2 rounded ${
              startIndex + itemsPerPage >= palmares.length
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-blue-500 hover:bg-blue-600 text-white"
            }`}
            disabled={startIndex + itemsPerPage >= palmares.length}
          >
            Suivant
          </button>
        </div>
      </div>
    </div>
  );
};

export default LastPalmares;
